import React, { useContext, useEffect, useMemo, useState } from 'react';
import '@stripe/stripe-js';
import { Box, Button, ButtonBase, Divider, Typography, ToggleButton, ToggleButtonGroup } from "@mui/material";
import FormBox from './building_blocks/FormBox';
import SurfaceCard from './building_blocks/SurfaceCard';
import ProviderCard from './building_blocks/ProviderCard';
import { currency, productUnitPrice } from './building_blocks/Currency';
import ProductPriceDisplay from './building_blocks/ProductPriceDisplay';
import { apiBaseUrl } from '../utils/Api/Api';
import ProductPhotoPlaceholder from '../images/placeholder_product_image.png';
import { useNavigate } from 'react-router-dom';
import cardsPathTo from '../utils/Api/cardsPathTo';
import ordersPathTo from '../utils/Api/ordersPathTo';
import campaignsPathTo from '../utils/Api/campaignsPathTo';
import visa from '../images/visa_card.png';
import master from '../images/master_card.png';
import SnackbarContext from '../contexts/SnackbarContext';
import { ShoppingCartProductsContext } from '../contexts/ShoppingCartProductsContext';
import { Elements } from '@stripe/react-stripe-js';
import NewCardDialog from './NewCardDialog';
import PaymentMethodsDialog from './PaymentMethodsDialog';
import stripeService from './stripeService';
import CheckoutPixPayment from './CheckoutPixPayment';
import { layoutButtonPillRadius, layoutCardRadius, layoutIconButtonRadius } from '../theme/colors';

/* Icons */
import { ArrowBackRounded } from '@mui/icons-material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import EditIcon from '@mui/icons-material/Edit';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';

// Re-enable when Stripe approves Pix on the platform account.
const PIX_CHECKOUT_ENABLED = false;

/* Padrão Informações */
const FieldInfo = ({ label, children }) => (
  /* Info Produto */
  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1, alignSelf: 'stretch' }}>
    {/* Título*/}
    <Typography variant="sectionTitle">
      {label}
    </Typography>

    {/* Informação*/}
    {children}
  </Box>
);

/* Padrão Preços */
const Price = ({ label, children, isTotal = false }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', alignSelf: 'stretch' }}>
    <Typography
      sx={{
        fontFamily: 'Nunito',
        fontSize: '14px',
        lineHeight: '24px',
        fontWeight: 400,
        color: isTotal ? 'text.primary' : 'text.secondary',
      }}
    >
      {label}
    </Typography>
    <Typography
      sx={{
        fontFamily: 'Nunito',
        fontSize: isTotal ? '18px' : '14px',
        lineHeight: '24px',
        fontWeight: isTotal ? 700 : 600,
        color: isTotal ? 'primary.main' : 'text.primary',
      }}
    >
      {children}
    </Typography>
  </Box>
);

const ShoppingCartCheckout = () => {
  const stripePromise = stripeService.getStripe();
  const provider = useMemo(() => JSON.parse(localStorage.getItem('shoppingProvider')), []);
  const checkoutProducts = useMemo(() => JSON.parse(localStorage.getItem('shoppingCartProducts')) || [], []);
  const totalAmount = checkoutProducts.reduce(
    (accumulator, product) => accumulator + product.cartQuantity * productUnitPrice(product),
    0
  );
  const deliveryFee = 1000  // 10 reais a entrega
  const applicationFeeRate = 0.10;
  const navigate = useNavigate()
  const totalQuantity = localStorage.getItem('totalQuantity');
  const currentUser = JSON.parse(localStorage.getItem('current_user'));
  const { openSnackbar } = useContext(SnackbarContext);
  const { setTotalQuantity } = useContext(ShoppingCartProductsContext);
  const { setTotalAmount } = useContext(ShoppingCartProductsContext);
  const { setShoppingCartProducts } = useContext(ShoppingCartProductsContext);
  const [openNewCardDialog, setOpenNewCardDialog] = useState(false);
  const [openPaymentMethodsDialog, setOpenPaymentMethodsDialog] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
  const [paymentKind, setPaymentKind] = useState('card');
  const [pixClientSecret, setPixClientSecret] = useState(null);
  const [pixInitLoading, setPixInitLoading] = useState(false);
  const [activeCampaign, setActiveCampaign] = useState(null);

  const amountAfterDiscount = (amount, discountType, discountValue) => {
    const value = Number(discountValue || 0);

    switch (discountType) {
      case 'percent':
        return Math.max(amount - Math.floor((amount * value) / 100), 0);
      case 'fixed_cents':
        return Math.max(amount - value, 0);
      case 'waive':
        return 0;
      default:
        return amount;
    }
  };

  const applicationFeeBase = Math.floor(totalAmount * applicationFeeRate);
  const deliveryFeeCharged = amountAfterDiscount(
    deliveryFee,
    activeCampaign?.delivery_fee_discount_type,
    activeCampaign?.delivery_fee_discount_value
  );
  const applicationFeeCharged = (() => {
    if (activeCampaign?.platform_fee_discount_type === 'percent') {
      const discountRate = Math.min(Number(activeCampaign.platform_fee_discount_value || 0) / 100, applicationFeeRate);
      return Math.floor(totalAmount * Math.max(applicationFeeRate - discountRate, 0));
    }

    return amountAfterDiscount(
      applicationFeeBase,
      activeCampaign?.platform_fee_discount_type,
      activeCampaign?.platform_fee_discount_value
    );
  })();
  const campaignDeliveryDiscountAmount = deliveryFee - deliveryFeeCharged;
  const campaignPlatformFeeDiscountAmount = applicationFeeBase - applicationFeeCharged;
  const totalDue = totalAmount + deliveryFeeCharged - campaignPlatformFeeDiscountAmount

  let selectedPaymentIndex = 0
  localStorage.getItem('payment_method') != null ? selectedPaymentIndex = localStorage.getItem('payment_method') : selectedPaymentIndex = 0

  useEffect(() => {
    cardsPathTo.list().then(result => {
      setPaymentMethods(result.data);
      setSelectedPaymentMethod(result.data[0]); // selecting first payment method for now
    });
    campaignsPathTo.active()
      .then(result => setActiveCampaign(result.data.campaign))
      .catch(() => setActiveCampaign(null));
    document.title = 'Carrinho de compras - TudoPets.app';
  }, [])

  const baseOrderPayload = () => ({
    provider_id: provider.id,
    total_amount: totalAmount,
    total_quantity: totalQuantity,
    products: checkoutProducts,
    delivery_address: currentUser.default_address.logradouro + ", " + currentUser.default_address.numero,
  });

  const handleCheckoutCard = () => {
    ordersPathTo
      .save({
        ...baseOrderPayload(),
        payment_kind: 'card',
        payment_method_id: selectedPaymentMethod.id,
      })
      .then(() => {
        clearShoppingCart();
        openSnackbar('Pedido criado. O pagamento será processado quando a loja aceitar.', 'success');
        navigate('/checkout-success');
      })
      .catch((err) => {
        const msg = err?.response?.data?.errors?.[0] || 'Não foi possível finalizar o pedido.';
        openSnackbar(msg, 'error');
      });
  };

  const handleStartPix = () => {
    setPixInitLoading(true);
    ordersPathTo
      .save({
        ...baseOrderPayload(),
        payment_kind: 'pix',
      })
      .then((result) => {
        const secret = result.data?.payment_intent_client_secret;
        if (!secret) {
          openSnackbar('Resposta do servidor sem client_secret do Pix.', 'error');
          return;
        }
        setPixClientSecret(secret);
        openSnackbar('Pedido criado. Conclua o Pix abaixo.', 'success');
      })
      .catch((err) => {
        const msg = err?.response?.data?.errors?.[0] || 'Não foi possível iniciar o Pix.';
        openSnackbar(msg, 'error');
      })
      .finally(() => setPixInitLoading(false));
  };

  const handlePixSuccess = () => {
    clearShoppingCart();
    openSnackbar('Pix concluído. A loja será avisada.', 'success');
    setPixClientSecret(null);
    navigate('/checkout-success');
  };

  const canFinalize =
    currentUser?.default_address &&
    (PIX_CHECKOUT_ENABLED
      ? paymentKind === 'pix' || (paymentKind === 'card' && selectedPaymentMethod)
      : selectedPaymentMethod);

  const clearShoppingCart = () => {
    localStorage.setItem("shoppingCartProducts", '[]');
    localStorage.setItem("totalAmount", '0');
    localStorage.setItem("totalQuantity", '0');
    setTotalAmount(0);
    setTotalQuantity(0);
    setShoppingCartProducts([]);
  }

  const clearShoppingCartAndGoToHome = () => {
    clearShoppingCart();
    openSnackbar("Carrinho esvaziado!", "success");
    navigate('/');
  }

  const handleCloseNewCardDialog = (paymentMethod) => {
    setOpenNewCardDialog(false);
    if (paymentMethod) {
      setPaymentMethods([...paymentMethods, paymentMethod]);
      setSelectedPaymentMethod(paymentMethod);
    }
  };

  const handleClosePaymentMethodsDialog = (paymentMethod = null) => {
    setOpenPaymentMethodsDialog(false);

    setSelectedPaymentMethod(paymentMethod);
  };

  const pillButtonSx = {
    m: 0,
    minHeight: 52,
    py: 1.5,
    px: 3,
    borderRadius: layoutButtonPillRadius,
    fontFamily: 'Nunito',
    fontSize: { xs: '16px', sm: '17px' },
    fontWeight: 700,
    lineHeight: 1.25,
    textTransform: 'none',
    boxShadow: (theme) => theme.shadows[2],
  };

  const checkoutActionsSx = {
    display: 'flex',
    flexDirection: { xs: 'column', sm: 'row' },
    alignItems: 'stretch',
    gap: 1.5,
    alignSelf: 'stretch',
    width: '100%',
  };

  if (PIX_CHECKOUT_ENABLED && pixClientSecret) {
    return (
      <Box>
        <Elements
          stripe={stripePromise}
          options={{
            clientSecret: pixClientSecret,
            appearance: { theme: 'stripe' },
            locale: 'pt-BR',
          }}
        >
          <Button onClick={() => setPixClientSecret(null)} sx={{ mb: 2, color: '#222' }}>
            ‹ Voltar ao carrinho
          </Button>
          <FormBox>
            <CheckoutPixPayment
              onSuccess={handlePixSuccess}
              onCancel={() => setPixClientSecret(null)}
            />
          </FormBox>
        </Elements>
      </Box>
    );
  }

  return (
    <Box>
      <Elements stripe={stripePromise}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>

          {/* Voltar e Título */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, alignSelf: 'stretch', position: 'relative' }}>
            {/* Botão para Voltar */}
            <Button data-no-shadow="true" onClick={() => navigate('/home')} sx={{ color: 'text.primary', borderRadius: layoutIconButtonRadius, m: '0' }}>
              <ArrowBackRounded />
            </Button>

            {/* Título */}
            <Typography variant="pageTitle" sx={{ width: 'auto', transform: 'translateX(-50%)', position: 'absolute', left: '50%' }}>Carrinho</Typography>
          </Box>

          {/* Loja */}
          <ProviderCard
            provider={provider}
            showProducts={false}
            showBanner={false}
            categories
            onCardClick={() => navigate(`/provider/${provider.id}`)}
          />

          {/* Endereço de Entrega */}
          <SurfaceCard sx={{ flexDirection: 'column', gap: 1 }}>
            <Typography sx={{ fontFamily: 'Nunito', fontSize: '14px', lineHeight: '24px', fontWeight: 400, color: 'text.secondary' }}>
              Entregar em:
            </Typography>
            {currentUser && currentUser.default_address &&
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', alignSelf: 'stretch' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LocationOnIcon sx={{ color: 'primary.main' }} />
                  <Typography sx={{ fontFamily: 'Nunito', fontSize: '14px', lineHeight: '24px', fontWeight: 700, color: 'text.primary' }}>
                    {currentUser.default_address.logradouro}, {currentUser.default_address.numero}
                  </Typography>
                </Box>
                <ButtonBase
                  onClick={() => navigate('/addresses', { state: { returnTo: '/shopping-cart' } })}
                  sx={{ display: 'flex', p: 1, alignItems: 'center', borderRadius: layoutCardRadius, bgcolor: 'surface.selectedHighlight' }}
                >
                  <EditIcon sx={{ color: 'primary.main' }} />
                </ButtonBase>
              </Box>
            }
            {currentUser && !currentUser.default_address &&
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', alignSelf: 'stretch' }}>
                <Button
                  onClick={() => navigate('/addresses/new?from=checkout', { state: { returnTo: '/shopping-cart' } })}>
                  Adicionar endereço de entrega
                </Button>
              </Box>
            }
          </SurfaceCard>

          {/* Produtos */}
          <FieldInfo label="Produtos">
            {checkoutProducts.map((product, index) => (
              <SurfaceCard key={product.id}>
                {/* Imagem Produto */}
                <img alt="product" style={{ width: '80px', height: '80px', borderRadius: '16px' }} src={product.item_photo ? `${apiBaseUrl}${product.item_photo}` : ProductPhotoPlaceholder} />

                {/* Infos Produto */}
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1, alignSelf: 'stretch' }}>

                  {/* Título Produto */}
                  <Typography variant="storeName">{product.title}</Typography>

                  {/* Descrição Produto */}
                  <Typography
                    textAlign="justify"
                    sx={{
                      display: '-webkit-box',
                      WebkitLineClamp: 1,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      wordBreak: 'break-word',
                      flex: 1,
                      fontFamily: 'Nunito',
                      fontSize: '14px',
                      lineHeight: '24px',
                      fontWeight: 400,
                      color: 'text.secondary',
                    }}
                  >
                    {product.description}
                  </Typography>

                  {/* Valor Produto */}
                  <Box>
                    <ProductPriceDisplay product={product} compact sx={{ color: 'text.primary' }} />
                    <Typography sx={{ fontFamily: 'Nunito', fontSize: '14px', lineHeight: '24px', fontWeight: 700, color: 'text.primary' }}>
                      {currency(productUnitPrice(product) * product.cartQuantity)} ({product.cartQuantity}x)
                    </Typography>
                  </Box>
                </Box>
              </SurfaceCard>
            ))}
          </FieldInfo>

          {/* Valores */}
          <FieldInfo label="Valores">
            <SurfaceCard sx={{ flexDirection: 'column' }}>
              {/*<Typography >Quantidade de itens: {totalQuantity}</Typography>
              <Typography >Taxa de entrega: {currency(deliveryFee)}</Typography>*/}

              {/* Subtotal */}
              <Price label="Subtotal:">
                {currency(totalAmount)}
              </Price>

              {/* Taxa de Entrega */}
              <Price label="Taxa de entrega:">
                {currency(deliveryFee)}
              </Price>

              {campaignDeliveryDiscountAmount > 0 && (
                <Price label="Desconto campanha na entrega:">
                  -{currency(campaignDeliveryDiscountAmount)}
                </Price>
              )}

              {campaignPlatformFeeDiscountAmount > 0 && (
                <Price label="Desconto campanha TudoPets.app:">
                  -{currency(campaignPlatformFeeDiscountAmount)}
                </Price>
              )}

              {activeCampaign && (
                <Typography variant="body2" sx={{ color: 'text.secondary', fontFamily: 'Nunito' }}>
                  Campanha aplicada:{' '}
                  <Typography component="span" sx={{ color: 'text.primary', fontWeight: 700 }}>
                    {activeCampaign.name}
                  </Typography>
                </Typography>
              )}

              <Divider sx={{ width: '100%' }} />

              {/* Total */}
              <Price label="Total:" isTotal>
                {currency(totalDue)}
              </Price>
            </SurfaceCard>
          </FieldInfo>

          {/* Forma de Pagamento */}
          <FieldInfo label="Forma de pagamento">
            <SurfaceCard sx={{ flexDirection: 'column', gap: 2 }}>
              {PIX_CHECKOUT_ENABLED && (
                <>
                  <ToggleButtonGroup
                    color="primary"
                    value={paymentKind}
                    exclusive
                    fullWidth
                    onChange={(e, value) => value && setPaymentKind(value)}
                    sx={{
                      '& .MuiToggleButton-root': { fontFamily: 'Nunito' },
                      '& .MuiButtonBase-root, & .MuiButtonBase-root:hover, & .MuiButtonBase-root:active': {
                        boxShadow: 'none',
                      },
                    }}
                  >
                    <ToggleButton value="card">Cartão (crédito ou débito)</ToggleButton>
                    <ToggleButton value="pix">Pix</ToggleButton>
                  </ToggleButtonGroup>

                  {paymentKind === 'pix' && (
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontFamily: 'Nunito' }}>
                      Compras internacionais podem incorrer em IOF de 3,5% cobrado pelo banco, conforme regras da Stripe/Ebanx.
                    </Typography>
                  )}
                </>
              )}

              {(!PIX_CHECKOUT_ENABLED || paymentKind === 'card') && (
                <>
                  {paymentMethods && paymentMethods.length === 0 && (
                    <Button
                      variant="contained"
                      color="secondary"
                      onClick={() => setOpenNewCardDialog(true)}
                      sx={{ ...pillButtonSx, width: '100%' }}
                    >
                      Adicionar cartão
                    </Button>
                  )}
                  {selectedPaymentMethod && (
                    <Box
                      sx={{
                        display: 'flex',
                        p: 2,
                        alignItems: 'center',
                        gap: 2,
                        alignSelf: 'stretch',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Box>
                        {selectedPaymentMethod.card.brand === 'visa' ? (
                          <img alt="visa" src={visa} style={{ width: '60px', height: '40px' }} />
                        ) : (
                          <img alt="master" src={master} style={{ width: '60px', height: '40px' }} />
                        )}
                      </Box>
                      <Typography>
                        **** {selectedPaymentMethod.card.last4} ·{' '}
                        {selectedPaymentMethod.card.funding === 'debit' ? 'Débito' : 'Crédito'}
                      </Typography>
                      <Box flexGrow={1} textAlign="right">
                        <ButtonBase data-no-shadow="true" onClick={() => setOpenPaymentMethodsDialog(true)}>
                          <KeyboardArrowRightIcon />
                        </ButtonBase>
                      </Box>
                    </Box>
                  )}
                </>
              )}
            </SurfaceCard>
          </FieldInfo>

          {/* Finalizar e Limpar Carrinho */}
          <Box sx={checkoutActionsSx}>
          <Button
              variant="contained"
              color="secondary"
              onClick={() => clearShoppingCartAndGoToHome()}
              sx={{
                ...pillButtonSx,
                flex: { sm: 1 },
                width: { xs: '100%', sm: 'auto' },
              }}
            >
              Limpar carrinho
            </Button>

            <Button
              variant="contained"
              color="primary"
              disabled={!canFinalize || (PIX_CHECKOUT_ENABLED && pixInitLoading)}
              onClick={() =>
                PIX_CHECKOUT_ENABLED && paymentKind === 'pix' ? handleStartPix() : handleCheckoutCard()
              }
              sx={{
                ...pillButtonSx,
                flex: { sm: 2 },
                width: { xs: '100%', sm: 'auto' },
              }}
            >
              {PIX_CHECKOUT_ENABLED && pixInitLoading
                ? 'Preparando Pix…'
                : PIX_CHECKOUT_ENABLED && paymentKind === 'pix'
                  ? 'Continuar com Pix'
                  : 'Finalizar pedido'}
            </Button>
          </Box>
        </Box>

        <NewCardDialog
          open={openNewCardDialog}
          onClose={handleCloseNewCardDialog}
        />
        <PaymentMethodsDialog open={openPaymentMethodsDialog} paymentMethods={paymentMethods} setPaymentMethods={setPaymentMethods} selectedPaymentIndex={selectedPaymentIndex} onClose={handleClosePaymentMethodsDialog} setOpenNewCardDialog={setOpenNewCardDialog} />
      </Elements>
    </Box>
  );
}

export default ShoppingCartCheckout
